import React, { useMemo } from 'react';
import {
  Button, ButtonColor, ButtonSize, ButtonVariant, ICON_ARROW_CIRCLE, ICON_MAP_MARKER, ICON_PHONE, Maps, Spacing, SpacingName, SymbolDirection, TypographyName,
} from '@doveit/lego';
import { NextPage } from 'next';
import AppLayout from '../client/layouts/app-layout/AppLayout';
import PageLayout, { PageLayoutSize } from '../client/layouts/page-layout/PageLayout';
import { template } from '../client/utils/string/string';
import PageHead, { PageHeadProps } from '../client/components/page-head/PageHead';
import * as domain from '../domain/graphcms';
import { REAL_ESTATE_AGENCY_BY_SLUG } from '../client/graphql/queries/realEstateAgencies';
import { assertNotNullOrUndefined } from '../client/utils/error/error';
import httpErrors from '../client/utils/error/http/httpErrors';
import {
  buildAgenciesURLObject,
  buildAgencyByLocalityURLObject,
  buildAgentURLObject,
  buildListingURLObject,
  buildRealEstateAgencyURLObject,
  buildSellPropertyURLObject,
  goToPage,
  relativeToAbsoluteURL,
} from '../client/utils/links';
import { SortOrder } from '../domain/pagination';
import { Status } from '../domain/status';
import { getLogger } from '../logger/isomorphic';
import { PropertySearchResult } from '../services/dtos/property';
import PageSection from '../client/components/page-section/PageSection';
import PropertySearchResultCardList from '../client/components/property-search-result-card-list/PropertySearchResultCardList';
import Intro from '../client/components/intro-V2/Intro';
import AgentList from '../client/components/agent-list/AgentList';
import ArticleMarkdown from '../client/components/article-markdown/ArticleMarkdown';
import variables from '../client/themes/variables';
import { getStructuredData, getBreadcrumbStructuredData, getAgencyStructuredData } from '../data/structured';
import { BreadcrumbItem } from '../client/components/breadcrumb/Breadcrumb';
import { CTA, TextAlign } from '../client/types';
import { PropertiesResponse, getAll } from '../services/properties/properties';
import { withError, withGetServerSideError } from '../client/hocs/with-server-side-error/withServerSideError';
import initGraphQLClient from '../client/graphql/initGraphQLClient';
import { importDynamicData } from '../services/importDynamicData';
import CardsHList from '../client/components/cards-hlist/CardsHList';
import SoldPropertyCard from '../client/components/sold-property-card/SoldPropertyCard';
import ReviewCard from '../client/components/review-card/ReviewCard';
import { DOVEIT_TRUSTPILOT_REVIEWS_PAGE } from '../client/containers/list-reviews/ListReviews';
import { goToExternalUrl } from '../client/utils/url/utils';
import { sortAgents } from '../services/agents/sortAgents';
import withNextConfig from '../client/hocs/withNextConfig/withNextConfig';
import CreateLeadSection from '../client/templates/common/create-lead-section/CreateLeadSection';
import { sendLeadSubmitEventAndRedirect } from '../client/utils/tracking';
import { Lead } from '../services/dtos';

export const AGENCY_PAGE_LEAD_SOURCE = 'personal';

const SOLD_PROPERTY_RESULT_SIZE = 3;

const EMPTY_PROPERTY_RESPONSE = {
  content: [],
  totalElements: 0,
} as unknown as PropertiesResponse;

export interface RealEstateAgencyProps {
  SEO: PageHeadProps['SEO'],
  realEstateAgency: domain.RealEstateAgency,
  intro: {
    title: string,
  },
  team: {
    title: string,
  },
  sellProperty: {
    title: string,
    cta: CTA,
  },
  latestPropertiesSection: {
    title: string,
    items: PropertySearchResult[],
  },
  soldPropertiesSection: {
    title: string,
    items: PropertySearchResult[],
  },
  reviewSection: {
    title: string,
    trustPilot: CTA,
    reviews: domain.Review[],
  },
  form: {
    title: string,
    intro: string,
    label: string,
  },
  config: {
    MAPS: {
      apiKey: string,
    },
  },
}

const logger = getLogger();

export const RealEstateAgency: NextPage<RealEstateAgencyProps> = ({
  SEO,
  intro,
  realEstateAgency,
  sellProperty,
  latestPropertiesSection,
  soldPropertiesSection,
  reviewSection,
  team,
  form,
  config,
}) => {
  const breadcrumb = useMemo<BreadcrumbItem[]>(() => [
    {
      label: 'Agenzie immobiliari',
      urlObject: buildAgenciesURLObject(),
    },
    {
      label: realEstateAgency.parent.locality.name,
      urlObject: buildAgencyByLocalityURLObject({
        locality: realEstateAgency.parent.locality.slug,
      }),
    },
    {
      label: realEstateAgency.name,
      urlObject: buildRealEstateAgencyURLObject({
        locality: realEstateAgency.parent.locality.slug,
        slug: realEstateAgency.slug,
      }),
    },
  ], [realEstateAgency]);

  const sellPropertyUrl = buildSellPropertyURLObject();

  const goToReviewPage = React.useCallback((url?: string) => () => {
    goToExternalUrl(url ?? DOVEIT_TRUSTPILOT_REVIEWS_PAGE);
  }, []);

  const onCreateLeadSuccess = React.useCallback((lead: Lead) => {
    sendLeadSubmitEventAndRedirect('main', lead);
  }, []);

  return (
    <AppLayout>
      <PageHead
        SEO={SEO}
        type="article"
        structuredData={
          getStructuredData(
            getBreadcrumbStructuredData(breadcrumb),
            realEstateAgency.address
              ? getAgencyStructuredData(realEstateAgency.address.locality, {
                ...realEstateAgency.address,
                url: SEO.canonical,
              })
              : undefined,
          )
        }
      />
      <PageLayout>
        <PageLayout.Wrapper maxWidth={PageLayoutSize.L}>
          <PageLayout.Breadcrumb items={breadcrumb} />
          <PageSection padding={[0, 0, SpacingName.BIGGER]}>
            <Intro
              title={intro.title}
              description={realEstateAgency.info}
              rightSlot={(
                <>
                  <Maps
                    apiKey={config.MAPS.apiKey}
                    title="Posizione agenzia"
                    params={`${realEstateAgency.address.streetAddress},${realEstateAgency.address.locality}`}
                    width="100%"
                    height="300px"
                  />

                  <Spacing margin={[SpacingName.SMALLER, 0, 0]}>
                    <Button
                      href={`http://www.google.com/maps?q=${realEstateAgency.address.streetAddress},${realEstateAgency.address.locality}`}
                      label={`${realEstateAgency.address.streetAddress} - ${realEstateAgency.address.locality}`}
                      variant={ButtonVariant.OUTLINE}
                      color={ButtonColor.NEUTRAL}
                      size={ButtonSize.SMALL}
                      icon={ICON_MAP_MARKER}
                      target="_blank"
                      rounded
                    />
                  </Spacing>
                  {realEstateAgency.phoneNumber && (
                    <Spacing margin={[SpacingName.SMALLER, 0, 0]}>
                      <Button
                        href={`tel:${realEstateAgency.phoneNumber}`}
                        label={realEstateAgency.phoneNumber}
                        variant={ButtonVariant.OUTLINE}
                        size={ButtonSize.SMALL}
                        icon={ICON_PHONE}
                        rounded
                      />
                    </Spacing>
                  )}
                </>
              )}
            />
          </PageSection>
        </PageLayout.Wrapper>

        <PageLayout.Content>
          <PageLayout.Main>
            <PageSection
              backgroundGradient={variables.sectionBackgroundGradient}
              padding={[SpacingName.BIG, 0, SpacingName.BIGGER]}
            >
              <PageSection.Wrapper>
                <PageSection.Title content={team.title} />
                <PageSection.Block>
                  <ArticleMarkdown>
                    {realEstateAgency.team}
                  </ArticleMarkdown>
                </PageSection.Block>
                <PageSection.Block>
                  <AgentList
                    agents={realEstateAgency.agents.map((agent) => ({
                      id: agent.id,
                      name: `${agent.name} ${agent.surname}`,
                      jobTitle: agent.jobTitleType.name,
                      picture: agent.photo,
                      urlObject: agent.biography && buildAgentURLObject({ slug: agent.slug }),
                      franchising: realEstateAgency.franchising,
                    }))}
                  />
                </PageSection.Block>
              </PageSection.Wrapper>
            </PageSection>

            {latestPropertiesSection?.items.length > 0 && (
              <PageSection
                padding={[SpacingName.BIGGER, 0]}
                data-ref="latest-properties-section"
              >
                <PageSection.Wrapper>
                  <PageSection.Title
                    size={TypographyName.H3}
                    content={latestPropertiesSection.title}
                  />
                </PageSection.Wrapper>
                <PageSection.Wrapper withoutPad>
                  <PageSection.Block>
                    <PropertySearchResultCardList
                      inline={3}
                      items={latestPropertiesSection.items.map((searchResult) => ({
                        searchResult,
                        urlObject: buildListingURLObject(searchResult),
                        onClick: () => goToPage(buildListingURLObject(searchResult)),
                      }))}
                    />
                  </PageSection.Block>
                </PageSection.Wrapper>
              </PageSection>
            )}

            {soldPropertiesSection.items.length > 0 && (
              <PageSection
                padding={[SpacingName.BIGGER, 0]}
                data-ref="sold-properties-section"
              >
                <PageSection.Wrapper>
                  <PageSection.Title
                    size={TypographyName.H3}
                    content={soldPropertiesSection.title}
                  />
                </PageSection.Wrapper>
                <PageSection.Wrapper withoutPad>
                  <PageSection.Block>
                    <CardsHList
                      inline={3}
                      sideSpacing={SpacingName.SMALL}
                    >
                      {soldPropertiesSection.items.map((searchResult) => (
                        <CardsHList.Item key={searchResult.id}>
                          <SoldPropertyCard
                            title={`${searchResult.locality}${(searchResult.suburb ? ` - ${searchResult.suburb}` : '')}`}
                            saving={searchResult.savingLabel}
                            image={searchResult.coverImageSet.small}
                            banner="Venduto"
                          />
                        </CardsHList.Item>
                      ))}
                    </CardsHList>
                  </PageSection.Block>
                </PageSection.Wrapper>
              </PageSection>
            )}

            {reviewSection.reviews.length > 0 && (
              <PageSection
                padding={[0, 0, SpacingName.BIGGER]}
                data-ref="reviews"
              >
                <PageSection.Wrapper>
                  <PageSection.Title
                    size={TypographyName.H3}
                    content={reviewSection.title}
                  />
                  <Spacing margin={[SpacingName.SMALLER, 0, 0]}>
                    <Button
                      href={reviewSection.trustPilot.href}
                      label={reviewSection.trustPilot.label}
                      rel="nofollow"
                      variant={ButtonVariant.LIGHT}
                      withoutSpacing
                      iconRight
                      iconDirection={SymbolDirection.RIGHT}
                      icon={ICON_ARROW_CIRCLE}
                      color={ButtonColor.PRIMARY}
                      dataRef={reviewSection.trustPilot.dataRef}
                      dataTracking={reviewSection.trustPilot.dataTracking}
                    />
                  </Spacing>
                </PageSection.Wrapper>
                <PageSection.Wrapper withoutPad>
                  <PageSection.Block>
                    <CardsHList
                      inline={3}
                      sideSpacing={SpacingName.SMALL}
                    >
                      {reviewSection.reviews.map((review) => (
                        <CardsHList.Item
                          key={review.id}
                        >
                          <ReviewCard
                            {...review}
                            onClick={goToReviewPage(review.url)}
                          />
                        </CardsHList.Item>
                      ))}
                    </CardsHList>
                  </PageSection.Block>
                </PageSection.Wrapper>
              </PageSection>
            )}

            <PageSection
              backgroundColor="neutrals.lighter"
              padding={[SpacingName.BIGGER, 0]}
            >
              <PageSection.Wrapper>
                <PageSection.Title
                  size={TypographyName.H2}
                  color="brand.primary"
                  content={sellProperty.title}
                />
                <PageSection.Block>
                  <ArticleMarkdown>
                    {realEstateAgency.evaluation}
                  </ArticleMarkdown>
                </PageSection.Block>
                <PageSection.Actions>
                  <Button
                    label={sellProperty.cta.label}
                    href={sellPropertyUrl.as}
                    onClick={() => goToPage(sellPropertyUrl)}
                    dataRef={sellProperty.cta.dataRef}
                    dataTracking={sellProperty.cta.dataTracking}
                  />
                </PageSection.Actions>
              </PageSection.Wrapper>
            </PageSection>

            <PageSection>
              <PageSection.Wrapper>
                <PageSection.Title
                  content={form.title}
                  textAlign={TextAlign.CENTER}
                />
                <PageSection.Description
                  text={form.intro}
                  textAlign={TextAlign.CENTER}
                  size={TypographyName.H4}
                  color="neutrals.dark"
                />
                <CreateLeadSection
                  title={form.label}
                  source={AGENCY_PAGE_LEAD_SOURCE}
                  campaign={realEstateAgency.slug}
                  dataRef="lead-form-main"
                  onSuccess={onCreateLeadSuccess}
                  dataTracking="lead-form-main-submit"
                />
              </PageSection.Wrapper>
            </PageSection>

          </PageLayout.Main>
        </PageLayout.Content>
      </PageLayout>
    </AppLayout>
  );
};

export const getServerSideProps = withGetServerSideError<RealEstateAgencyProps>(async ({ query }) => {
  const graphQLClient = initGraphQLClient();
  const { slug, locality } = query;
  const [
    pageData,
    { data },
  ] = await Promise.all([
    importDynamicData('pages/real-estate-agency.yml'),
    graphQLClient.query<{ realEstateAgency: domain.RealEstateAgency, reviews: domain.Review[] }>({
      query: REAL_ESTATE_AGENCY_BY_SLUG,
      variables: {
        slug,
      },
    }),
  ]);

  const { realEstateAgency, reviews } = data;

  assertNotNullOrUndefined(
    realEstateAgency,
    new httpErrors.NotFound(`Cannot find a real estate agency with slug: ${slug}`),
  );

  assertNotNullOrUndefined(
    realEstateAgency.address,
    new httpErrors.InternalServerError(`Real estate agency with slug: ${slug} has not an associated address`),
  );

  assertNotNullOrUndefined(
    realEstateAgency.parent,
    new httpErrors.InternalServerError(`Real estate agency with slug: ${slug} has not an associated parent`),
  );

  let latestProvinceSoldProperties: PropertySearchResult[] = [];

  const teamEmails: string[] = realEstateAgency.agents.map((agent) => agent.email);
  const isAFranchisingAgency = realEstateAgency.franchising;

  const [{ content: latestTeamProperties }, { content: latestTeamSoldProperties }] = await Promise.all([
    getAll(
      {
        status: Status.LIVE,
        agentEmail: teamEmails,
      },
      {
        size: 9,
        sort: {
          published_at: SortOrder.DESC,
        },
      },
    ).catch((err) => {
      logger.warn({ err }, 'Error while retrieving properties by team emails');
      return EMPTY_PROPERTY_RESPONSE;
    }),
    getAll(
      {
        status: Status.VENDUTO,
        agentEmail: teamEmails,
      },
      {
        size: SOLD_PROPERTY_RESULT_SIZE,
        sort: {
          sold_at: SortOrder.DESC,
        },
      },
    ).catch((err) => {
      logger.warn({ err }, 'Error while retrieving sold properties by team emails');
      return EMPTY_PROPERTY_RESPONSE;
    }),
  ]);

  if (!isAFranchisingAgency && latestTeamSoldProperties.length < SOLD_PROPERTY_RESULT_SIZE) {
    latestProvinceSoldProperties = (await getAll({
      status: Status.VENDUTO,
      province: realEstateAgency.parent.locality.province.slug,
      exclude: latestTeamSoldProperties.map((property) => property.id),
    }, {
      size: SOLD_PROPERTY_RESULT_SIZE,
      sort: {
        sold_at: SortOrder.DESC,
      },
    }).catch((err) => {
      logger.warn({ err }, 'Error while retrieving latest province sold properties');
      return EMPTY_PROPERTY_RESPONSE;
    })).content;
  }

  const actual = buildRealEstateAgencyURLObject({
    locality: locality as string,
    slug: slug as string,
  });

  const agencyURLObject = buildRealEstateAgencyURLObject({
    locality: realEstateAgency.parent.locality.slug,
    slug: slug as string,
  });

  if (actual.as !== agencyURLObject.as) {
    return {
      redirect: {
        destination: agencyURLObject.as,
        permanent: false,
      },
    };
  }

  const {
    SEO,
    sellProperty,
    latestPropertiesSection,
    soldPropertiesSection,
    reviewSection,
    intro,
    team,
    form,
  } = template(pageData, {
    locality: realEstateAgency.address.locality,
    name: realEstateAgency.name,
  });

  const soldProperties: PropertySearchResult[] = [...latestTeamSoldProperties, ...latestProvinceSoldProperties].slice(0, SOLD_PROPERTY_RESULT_SIZE);

  return {
    props: {
      SEO: {
        ...SEO,
        canonical: relativeToAbsoluteURL(agencyURLObject.as),
      },
      realEstateAgency: {
        ...realEstateAgency,
        agents: sortAgents(realEstateAgency.agents),
      },
      sellProperty,
      latestPropertiesSection: {
        ...latestPropertiesSection,
        items: latestTeamProperties,
      },
      soldPropertiesSection: {
        ...soldPropertiesSection,
        items: soldProperties,
      },
      reviewSection: {
        ...reviewSection,
        reviews,
      },
      intro,
      team,
      form,
    } as RealEstateAgencyProps,
  };
});

export default withError(withNextConfig(RealEstateAgency));
